import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import mapValues from 'lodash/mapValues';
import omitBy from 'lodash/omitBy';
import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { handleCancelError, refreshAbortController } from '@/apis/axios.utils';
import { useListeningPermissions } from '@/app/socialListening/composables/useListeningPermissions';
import { useSocialListening } from '@/app/socialListening/composables/useSocialListening';
import {
  POST_SORT_OPTIONS,
  POST_VOLUME_GRAPH_OPTIONS,
  TOP_POSTS_INITIAL_MAX_POSTS,
  TOP_POSTS_STATE_KEY,
} from '@/app/socialListening/constants';
import {
  CHANNEL_FILTER_OPTIONS,
  CUSTOM_DATE_FILTER_OPTION,
  TRENDS_DEFAULT_FILTERS,
} from '@/app/socialListening/constants/listeningFilters';
import {
  getKeywordORString,
  getKeywordTypePayload,
  getStartAndEndDate,
} from '@/app/socialListening/utils/api-request-utils';
import { convertToSearchBodyFilters } from '@/app/socialListening/utils/filter-utils';
import {
  getFormattedKeywordPostVolumeTimeSeriesChartData,
  getFormattedSentimentChartData,
} from '@/app/socialListening/utils/formatters';
import { groupByTotalPostsPerPeriodWithPrimaryBreakdown } from '@/app/socialListening/utils/graph-utils';
import { originForReport } from '@/config';
import { useAuthStore } from '@/stores/auth';
import { usePdfStore } from '@/stores/pdf';
import { useSocialListeningStore } from '@/stores/social-listening';
import { useSocialListeningTopPostsStore } from '@/stores/social-listening-top-posts';
import { useSocketStore } from '@/stores/socket';
import { useTrackingStore } from '@/stores/tracking';
import { stringifyQuery } from '@/utils/query';

export const useSocialListeningTrendsStore = defineStore('socialListeningTrends', () => {
  const socialListeningStore = useSocialListeningStore();
  const authStore = useAuthStore();
  const trackingStore = useTrackingStore();
  const pdfStore = usePdfStore();
  const route = useRoute();
  const socialListeningTopPostsStore = useSocialListeningTopPostsStore();
  const socketStore = useSocketStore();
  const { canAccessYoutubeCompliance, canAccessSocialListeningGDI } = useListeningPermissions();
  const socialListeningGDI = useSocialListening();

  // Refs
  const filters = ref({ ...cloneDeep(TRENDS_DEFAULT_FILTERS) });
  const temporaryFilters = ref({});

  const sentimentTrendTimeSeries = ref(null);

  const pending = ref({
    sentimentTrendTimeSeries: false,
    keywordPostVolumeTimeSeries: false,
  });

  const queryKeyword = ref('');
  const keywordPostVolumeTimeSeries = ref({ meters: null, tooltipData: null });
  const abortControllers = { keywordPostVolumeTimeSeries: ref(null) };

  // Computed
  const searchFilters = computed(() => ({
    ...(filters.value || {}),
    ...(temporaryFilters.value || {}),
  }));

  const sourceCreated = computed(() => {
    if (searchFilters.value.dateRange === CUSTOM_DATE_FILTER_OPTION.value) {
      return searchFilters.value.customDateRange;
    }
    const { startDate, endDate } = getStartAndEndDate(searchFilters.value.dateRange);
    return { onOrAfter: startDate, onOrBefore: endDate };
  });

  function getChannels(channels = []) {
    let filteredChannels = channels;
    if (!canAccessYoutubeCompliance.value) {
      filteredChannels = filteredChannels.filter((c) => c !== CHANNEL_FILTER_OPTIONS.YOUTUBE.value);
    }
    return filteredChannels;
  }

  const selectedChannels = computed(() => {
    const channels = searchFilters.value.channels?.length
      ? searchFilters.value.channels
      : TRENDS_DEFAULT_FILTERS.channels;
    return getChannels(channels);
  });

  const selectedMediaTypes = computed(() =>
    searchFilters.value.mediaTypes?.length
      ? searchFilters.value.mediaTypes
      : TRENDS_DEFAULT_FILTERS.mediaTypes,
  );

  const formattedFilters = computed(() => {
    const converted = convertToSearchBodyFilters({
      sources: selectedChannels.value,
      sentiment: searchFilters.value.sentiments,
      mediaTypes: selectedMediaTypes.value,
      industryIds: canAccessSocialListeningGDI.value ? searchFilters.value.industryIds : undefined,
      locations: searchFilters.value.locations,
    });
    return {
      sourceCreated: sourceCreated.value,
      keywordsAndHashtags: getKeywordORString(
        searchFilters.value,
        socialListeningStore.topKeywords,
      ),
      ...omitBy(converted, isEmpty),
    };
  });

  const _serializeFilterValue = (key, value) => {
    if (key === 'channels') {
      return selectedChannels.value?.join(',') || '';
    }
    if (key === 'locations') {
      return value;
    }
    return Array.isArray(value) ? value.join(',') : value;
  };

  const drawerSerializedFilters = computed(() => {
    const drawerFilters = {
      ...omitBy(searchFilters.value, isEmpty),
      keywordsAndHashtags: getKeywordORString(filters.value, socialListeningStore.topKeywords),
    };
    return mapValues(drawerFilters, (value, key) => _serializeFilterValue(key, value));
  });

  const scale = computed(() => socialListeningStore.calculateScale(formattedFilters.value));

  const postVolumePayload = computed(() => ({
    scale: scale.value,
    searchFilters: formattedFilters.value,
  }));

  // Functions
  function setFilters({
    channels,
    sentiments,
    mediaTypes,
    dateRange,
    keyword,
    keywordTypes,
    customDateRange,
    industryIds,
    locations,
  }) {
    filters.value = {
      channels: channels ? getChannels(channels) : channels,
      sentiments,
      mediaTypes,
      dateRange,
      keyword,
      keywordTypes,
      customDateRange,
      ...(canAccessSocialListeningGDI.value ? { industryIds } : {}),
      locations,
    };
  }

  function resetFilters() {
    filters.value = { ...cloneDeep(TRENDS_DEFAULT_FILTERS) };
    queryKeyword.value = '';
  }

  function setKeywordFilter(keyword) {
    filters.value = {
      ...filters.value,
      keyword,
    };
  }

  function removeKeywordFilter() {
    filters.value = {
      ...filters.value,
      keyword: '',
    };
    socialListeningStore.enableTopLevelFilters();
  }

  function clearTempFilters() {
    temporaryFilters.value = {};
  }

  function setTempFilters(filtersObj) {
    temporaryFilters.value = { ...filtersObj };
  }

  function clearTimeSeriesData() {
    sentimentTrendTimeSeries.value = null;
    keywordPostVolumeTimeSeries.value = {};
  }

  async function getSentimentTrend() {
    try {
      sentimentTrendTimeSeries.value = null;
      pending.value.sentimentTrendTimeSeries = true;
      const result = await socialListeningStore.getSentimentTrendTimeSeries({
        searchFilters: formattedFilters.value,
        scale: scale.value,
      });
      sentimentTrendTimeSeries.value = getFormattedSentimentChartData(result, filters.value);
    } finally {
      pending.value.sentimentTrendTimeSeries = false;
    }
  }

  async function getTimeSeriesPostVolumeKeyword() {
    pending.value.keywordPostVolumeTimeSeries = true;
    let requestCancelled = false;
    try {
      const graphOptions = POST_VOLUME_GRAPH_OPTIONS.TOTAL_POSTS;
      keywordPostVolumeTimeSeries.value = {};
      const payload = {
        searchFilters: formattedFilters.value,
        scale: scale.value,
        breakdownBy: graphOptions.primaryBreakdown,
      };
      const signal = refreshAbortController(abortControllers.keywordPostVolumeTimeSeries);
      const result = await socialListeningGDI.getTimeSeriesPostVolume(payload, signal);
      const breakdownGroups = groupByTotalPostsPerPeriodWithPrimaryBreakdown(
        result,
        [],
        graphOptions.value,
        [],
        scale.value,
      );
      keywordPostVolumeTimeSeries.value = getFormattedKeywordPostVolumeTimeSeriesChartData(
        breakdownGroups,
        graphOptions.chartConfig,
      );
    } catch (error) {
      handleCancelError(error, () => {
        requestCancelled = true;
      });
    } finally {
      if (!requestCancelled) {
        pending.value.keywordPostVolumeTimeSeries = false;
      }
    }
  }

  async function getTopPostsPreview({
    stateKey = TOP_POSTS_STATE_KEY.TRENDS_TOP_POSTS_PREVIEW,
    offset = 0,
    limit = TOP_POSTS_INITIAL_MAX_POSTS,
    ignoreKeyword = false,
  }) {
    const topPostsFilters = { ...formattedFilters.value };
    if (
      [
        TOP_POSTS_STATE_KEY.TRENDS_DRAWER_TOP_POSTS,
        TOP_POSTS_STATE_KEY.TRENDS_DRAWER_TOP_POSTS_PREVIEW,
      ].includes(stateKey) &&
      temporaryFilters.value.keyword
    ) {
      topPostsFilters.keywordsAndHashtags = temporaryFilters.value.keyword;
    }
    if (ignoreKeyword && socialListeningStore.topKeywords) {
      topPostsFilters.keywordsAndHashtags = getKeywordORString(
        {},
        socialListeningStore.topKeywords,
      );
    }
    await socialListeningTopPostsStore.fetchTopPosts(stateKey, {
      filters: topPostsFilters,
      sorts: [`-${POST_SORT_OPTIONS.engagements.value}`, `-${POST_SORT_OPTIONS.date.value}`],
      paging: {
        limit,
        offset,
      },
    });
  }

  async function getMentionsBreakdown(graphOption = POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_CHANNEL) {
    const payload = {
      ...postVolumePayload.value,
      breakdownBy: graphOption.primaryBreakdown,
    };
    await socialListeningStore.getTimeSeriesPostVolume(payload, graphOption.value);
  }

  async function fetchPageData(skipTopKeywords = false) {
    if (!skipTopKeywords) {
      const { keywordsAndHashtags, ...filterData } = formattedFilters.value;
      const payload = {
        searchFilters: { ...filterData },
        ...getKeywordTypePayload(searchFilters.value.keywordTypes),
      };
      await socialListeningStore.getTopKeywords(payload);
    }

    if (socialListeningStore.topKeywords.length !== 0) {
      const promises = [];
      promises.push(getTopPostsPreview({ ignoreKeyword: true }));
      if (socketStore?.id) {
        const socketId = socketStore.id;
        const requestId = uuidv4();
        promises.push(
          socialListeningStore.getVisualTrends({
            requestId,
            socketId,
            searchBody: {
              filters: { ...formattedFilters.value },
            },
          }),
        );
      }
      await Promise.allSettled(promises);
    } else {
      socialListeningStore.clearOverviewData(true);
      socialListeningTopPostsStore.clearTopPostsPreview();
    }
  }

  function downloadPDFReport() {
    const queryParams = mapValues(filters.value, (value, key) => _serializeFilterValue(key, value));
    const params = {};
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (typeof value === 'object') {
        params[key] = JSON.stringify(value);
      } else {
        params[key] = String(value);
      }
    });

    const { path } = route;
    const url = `${originForReport()}${path}/report?${stringifyQuery({
      ...params,
      brand: authStore.currentBrand.label,
    })}`;

    const filename = 'social-listening-trends';
    const type = 'socialListeningTrends';
    pdfStore.getPdfV2({
      filename,
      notificationSocketId: socketStore.id,
      idleMaxInflightRequest: 2,
      idleTimeout: 20,
      notificationMeta: JSON.stringify({ type }),
      height: '1120px',
      width: '1500px',
      url,
      landscape: true,
    });
    trackingStore.track('Report Downloaded', {
      type: 'Social Listening - Trends',
      fileName: filename,
      format: 'PDF',
    });
  }

  return {
    filters,
    temporaryFilters,
    searchFilters,
    drawerSerializedFilters,
    queryKeyword,
    removeKeywordFilter,
    setFilters,
    resetFilters,
    setKeywordFilter,
    clearTempFilters,
    setTempFilters,
    pending,
    getSentimentTrend,
    sentimentTrendTimeSeries,
    sourceCreated,
    getChannels,
    selectedChannels,
    formattedFilters,
    scale,
    keywordPostVolumeTimeSeries,
    getTimeSeriesPostVolumeKeyword,
    fetchPageData,
    getTopPostsPreview,
    getMentionsBreakdown,
    clearTimeSeriesData,
    downloadPDFReport,
  };
});
